import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { GlobalSearch } from "@/components/site/GlobalSearch";
import { useAuth } from "@/lib/auth";

const NAV = [
    { to: "/", label: "Home", exact: true },
    { to: "/about", label: "About" },
    { to: "/committee", label: "Committee" },
    { to: "/societies", label: "Societies" },
    { to: "/documents", label: "Documents" },
    { to: "/gallery", label: "Gallery" },
    { to: "/events", label: "Events" },
    { to: "/news", label: "News" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [navigate]);

    return (
        <header
            className={`sticky top-0 z-40 glass-nav ${scrolled ? "shadow-sm" : ""}`}
            data-testid="site-navbar"
        >
            <div className="container-x flex h-16 items-center justify-between gap-4 md:h-20">
                <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo-link">
                    <Logo size={44} />
                    <div className="hidden leading-tight sm:block">
                        <div className="font-serif text-lg font-semibold text-brand-blue">Gaurav Sankalp</div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-slate-500">Phase-1 Association</div>
                    </div>
                </Link>

                <nav className="hidden items-center gap-1 xl:flex" data-testid="desktop-nav">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.exact}
                            data-testid={`nav-link-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                                    isActive ? "bg-brand-blue-light text-brand-blue" : "text-slate-700 hover:text-brand-blue"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        aria-label="Search"
                        data-testid="navbar-search-btn"
                        className="rounded-full p-2 text-slate-700 hover:bg-slate-100"
                        onClick={() => setSearchOpen(true)}
                    >
                        <Search size={18} />
                    </button>
                    {user ? (
                        <div className="hidden items-center gap-2 md:flex">
                            <Link to="/admin" className="btn-outline !py-2" data-testid="navbar-dashboard-link">Dashboard</Link>
                            <button className="btn-ghost" onClick={logout} data-testid="navbar-logout-btn">Logout</button>
                        </div>
                    ) : (
                        <Link to="/admin/login" className="btn-primary !py-2 hidden md:inline-flex" data-testid="navbar-admin-link">
                            Admin
                        </Link>
                    )}
                    <button
                        className="rounded-full p-2 text-slate-700 hover:bg-slate-100 xl:hidden"
                        aria-label="Menu"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="navbar-menu-btn"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="border-t border-slate-200 bg-white xl:hidden" data-testid="mobile-nav">
                    <div className="container-x flex flex-col py-3">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.exact}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `rounded-lg px-4 py-3 text-sm font-medium ${
                                        isActive ? "bg-brand-blue-light text-brand-blue" : "text-slate-700 hover:bg-slate-50"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <div className="mt-2 flex gap-2 px-2">
                            {user ? (
                                <>
                                    <Link to="/admin" className="btn-outline flex-1" onClick={() => setOpen(false)}>Dashboard</Link>
                                    <button className="btn-ghost" onClick={() => { logout(); setOpen(false); }}>Logout</button>
                                </>
                            ) : (
                                <Link to="/admin/login" className="btn-primary flex-1" onClick={() => setOpen(false)}>Admin Login</Link>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
        </header>
    );
};

export default Navbar;
